import { trackById } from 'src/app/shared/utils/track-by.util';
import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SensorService } from '../../services/sensor.service';
import { LoaderService } from '../../services/loader.service';
import { ToastService } from '../../services/toast.service';
import { SensorInfo, SensorModel } from '../../models/sensor.model';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { BatteryService } from 'src/app/services/battery.service';
import { ChartService } from 'src/app/services/chart.service';
import { take } from 'rxjs/internal/operators/take';
import { finalize } from 'rxjs/internal/operators/finalize';
import { CalibrationDialogComponent } from '../calibration-dialog/calibration-dialog.component';
import { MatDialog } from '@angular/material/dialog';

enum DeviceInfoView {
  DeviceDetails = 'device_details',
  DeviceHistory = 'device_history',
  CalibrationHistory = 'calibration_history',
  ParameterMeasured = 'parameter_measured'
}

@Component({
  standalone: false,
  selector: 'app-device-info',
  templateUrl: './device-info.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./device-info.component.scss']
})
export class DeviceInfoComponent implements OnInit {
  selectedDeviceData: SensorModel;
  deviceInfo: SensorInfo;
  deviceInfoView: DeviceInfoView;
  DeviceInfoViewEnum = DeviceInfoView;
  batteryInfo: { imgSrc: string; tooltip: string } = { imgSrc: '', tooltip: '' };

  constructor(
    @Inject(FLYOUT_DATA) public data: SensorModel,
    private toastService: ToastService,
    private sensorService: SensorService,
    private loaderService: LoaderService,
    protected dateFormatService: DateFormatService,
    private batteryService: BatteryService,
    private chartService: ChartService,
    private dialog: MatDialog
  ) {
    this.selectedDeviceData = data;
  }

  ngOnInit(): void {
    this.deviceInfoView = DeviceInfoView.DeviceDetails;
    this.batteryInfo = this.getBatteryInfo();
    this.loadData();
  }

  loadData() {
    this.loaderService.show();
    this.sensorService
      .getSensorInfo(this.selectedDeviceData.id)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: (data: SensorInfo) => {
          this.deviceInfo = data;
          this.deviceInfo.parameters = [];
          this.selectedDeviceData.parameterTypes.forEach((types) => {
            this.deviceInfo.parameters.push({
              id: types,
              name: this.chartService.getParameterName(types),
            });
          });
          this.deviceInfo.installationDateFormatted = this.selectedDeviceData.installationDate
            ? this.selectedDeviceData.installationDate.toString()
            : null;
          this.deviceInfo.nextCalibrationFormatted = this.deviceInfo.nextCalibration
            ? this.dateFormatService.formatTimestampToString(this.deviceInfo.nextCalibration, this.dateFormatService.shortDateFormat)
            : null;
          this.deviceInfo.lastCalibratedFormatted = this.deviceInfo.lastCalibrated
            ? this.dateFormatService.formatTimestampToString(this.deviceInfo.lastCalibrated, this.dateFormatService.shortDateFormat)
            : null;
        },
        error: (error) => {
          this.toastService.showError(error);
        }
      });
  }

  switchView(view: DeviceInfoView) {
    this.deviceInfoView = view;
  }

  getBatteryInfo(): { imgSrc: string, tooltip: string } {
    const batteryPercentage = this.selectedDeviceData?.batteryPercentage ?? null;
    const batteryCharging = this.selectedDeviceData?.batteryCharging ?? null;
    const batteryImgSrcs = this.batteryService.getBatteryImgSrcsCommon(batteryPercentage, batteryCharging);
    const batteryTooltips = this.batteryService.getBatteryTooltipsCommon(batteryPercentage, batteryCharging);

    return {
      imgSrc: batteryImgSrcs.length > 0 ? batteryImgSrcs[0] : '',
      tooltip: batteryTooltips.length > 0 ? batteryTooltips[0] : '',
    };
  }

  public trackById = trackById;

  openAddCalibrationPopup() {
    if (!this.deviceInfo?.id) return;
    this.dialog.open(CalibrationDialogComponent, {
      data: {
        sensorId: this.deviceInfo.id
      },
      width: '460px',
      autoFocus: false
    })
  }
}
